import { GetMonthsPopularPerks } from "$gql/queries/general/GetMonthsPopularPerks.gen";
import { getContentfulClient } from "@tiicker/util/lib/contentful/client";
import {
  ContentSections,
  getImageReference,
  getItemReference,
  loadContentSections,
} from "@tiicker/util/lib/contentful/helpers";
import { RecentPerksOfTheWeek } from "@tiicker/util/lib/contentful/queries";
import {
  ContentfulContentType,
  ContentfulKey,
  GenericMarketingPage,
  PageSection,
  PageSectionKey,
  PerkDocument,
  PerkOfTheWeekDocument,
} from "@tiicker/util/lib/contentful/types";
import PerksPageCTA from "components/Perks/PerksPageCTA/PerksPageCTA";
import FooterV2 from "components/FooterV2/FooterV2";
import GeneralPageHeader from "components/Global/Headers/GeneralPageHeader/GeneralPageHeader";
import NavigationBar from "components/Global/Navigation/NavigationBar/NavigationBar";
import ColorWrapper from "components/Home/ColorWrapper/ColorWrapper";
import GenericMetaTags from "components/MetaTags/GenericMetaTags";
import NewPerksFromBrands from "components/Perks/NewPerksFromBrands/NewPerksFromBrands";
import PerkSearch from "components/Perks/PerkSearch/PerkSearch";
import PerksOfTheWeek from "components/Perks/PerksOfTheWeek/PerksOfTheWeek";
import PopularPerkSlider from "components/Perks/PopularPerkSlider/PopularPerkSlider";
import TiiCKERPerksCTA from "components/TiiCKERPerks/CallToAction/TiiCKERPerksCTA";
import { Entry, EntryCollection } from "contentful";
import { compact } from "lodash";
import { GetStaticProps } from "next";
import React, { useState } from "react";
import { useFetchUser, useQueryBundle } from "source/hooks";
import RecentlyClaimedPerks from "components/Perks/RecentlyClaimedPerks/RecentlyClaimedPerks";
import { GetRecentlyClaimedPerks } from "$gql/queries/general/GetRecentlyClaimedPerks.gen";
import VoteForPerksModal from "components/Perks/VoteForPerkModal/VoteForPerksModal";
import LoginModal from "components/LoginModal/LoginModal";
import * as TimeDateIso from "@tiicker/util/lib/date-iso/time-date-iso";
import { useContentfulEntries } from "source/contentful/provider";
import StartEarningPerksMadi from "components/StartEarningPerksMadi/StartEarningPerksMadi";

type Props = {
  pageContent: EntryCollection<GenericMarketingPage>;
  pageSections: Entry<PageSection>[];
  contentSections: ContentSections;
  perksOfTheWeek: EntryCollection<PerkOfTheWeekDocument>;
};

const Perks = (props: Props) => {
  const [votePerkModal, setVotePerkModal] = useState<boolean>(false);
  const { user, loading } = useFetchUser();

  const today = TimeDateIso.now();
  const newPerksFromBrands = useContentfulEntries<PerkDocument>(
    ContentfulContentType.Perk,
    {
      query: {
        content_type: ContentfulContentType.Perk,
        "fields.startDate[lte]": today,
        "fields.endDate[gte]": today,
        order: "-fields.startDate",
      },
    }
  );

  const distinctBrandTickers =
    newPerksFromBrands.state === "DONE"
      ? Array.from(
          new Set(
            newPerksFromBrands.result.items.map(
              (x) => x.fields.brand.fields.tickerSymbol
            )
          )
        )
      : [];

  const destructuredBrands =
    newPerksFromBrands.state === "DONE"
      ? compact(
          distinctBrandTickers.map((ticker) => {
            const firstPerk = newPerksFromBrands.result.items.find(
              (x) => x.fields.brand.fields.tickerSymbol === ticker
            );

            if (!firstPerk) {
              return undefined;
            }

            const brandLogo = getImageReference(
              firstPerk.fields.brand.fields.companyLogoDark,
              newPerksFromBrands.result.includes
            );
            return {
              tickerSymbol: ticker,
              logo: brandLogo?.fields.file.url ?? "",
              name: firstPerk.fields.brand.fields.brandName,
              perkSummary: firstPerk.fields.brand.fields.perkSummary,
            };
          })
        )
      : [];

  const pageEntry =
    props.pageContent.items.length > 0 ? props.pageContent.items[0] : undefined;

  const favoritesContent = props.pageSections.find(
    (section) => section.fields.sectionKey === PageSectionKey.PerksFavorites
  );

  const voteContent = props.pageSections.find(
    (section) => section.fields.sectionKey === PageSectionKey.PerksVote
  );

  const browseAllContent = props.pageSections.find(
    (section) => section.fields.sectionKey === PageSectionKey.PerksBrowseAll
  );
  const tiickerPerkCTA = props.pageSections.find(
    (section) => section.fields.sectionKey === PageSectionKey.TiiCKERPerkCTA
  );

  const popularPerks = useQueryBundle(GetMonthsPopularPerks);
  const recentlyClaimedPerks = useQueryBundle(GetRecentlyClaimedPerks);

  const cta = props.pageSections.find(
    (x) => x.fields.sectionKey === PageSectionKey.JoinNowCTA
  );

  const openVotePerkModal = () => {
    setVotePerkModal(true);
  };

  const closeVotePerkModal = () => {
    setVotePerkModal(false);
  };

  return (
    <>
      {pageEntry && <GenericMetaTags pageContent={pageEntry.fields} />}
      <ColorWrapper displayOptions={{ marginBottom: 80 }}>
        <NavigationBar />
        <GeneralPageHeader
          title={pageEntry?.fields.displayTitle ?? "Own stock? Get perks."}
          description={
            pageEntry?.fields.displayDescription ??
            "Discover the brands who offer their shareholders exclusive perks like product discounts, VIP events and so much more!"
          }
        />
        <PerkSearch />
      </ColorWrapper>
      <TiiCKERPerksCTA
        qualifyContent={tiickerPerkCTA?.fields.description}
        loggedOutContent={tiickerPerkCTA?.fields.displayTitle}
      />
      <PerksOfTheWeek
        perksOfTheWeek={props.perksOfTheWeek.items}
        perksOfTheWeekIncludes={props.perksOfTheWeek.includes}
        eligiblePerkIds={[]}
        claimedPerkIds={[]}
        canClaimPerks={true}
      />
      {popularPerks.state === "DONE" && (
        <PopularPerkSlider perkIds={popularPerks.data.getMonthsPopularPerks} />
      )}
      <div className="container">
        {destructuredBrands.length > 0 && (
          <NewPerksFromBrands
            brands={destructuredBrands.slice(0, 4)}
            loadingCount={4}
            title="New perks from these brands"
          />
        )}
      </div>
      {voteContent && (
        <PerksPageCTA
          title={voteContent.fields.displayTitle ?? ""}
          description={voteContent.fields.description ?? ""}
          onClick={openVotePerkModal}
          buttonText="Vote Now"
        />
      )}
      {user ? (
        <VoteForPerksModal
          visible={votePerkModal}
          onClose={closeVotePerkModal}
          description={voteContent?.fields.description}
          title={voteContent?.fields.displayTitle}
          userId={user.id}
        />
      ) : (
        <LoginModal
          show={votePerkModal}
          showModal={setVotePerkModal}
          description="Please sign in or sign up before voting for perks!"
        />
      )}
      {recentlyClaimedPerks.state === "DONE" && (
        <RecentlyClaimedPerks
          perkIds={recentlyClaimedPerks.data.getRecentlyClaimedPerks}
        />
      )}
      {browseAllContent && (
        <PerksPageCTA
          title={browseAllContent.fields.displayTitle ?? ""}
          description={browseAllContent.fields.description ?? ""}
          href={`/perks/all`}
          icon="/icons/Gift.png"
          buttonText="View All Perks"
        />
      )}

      {/* <PerkPlayingCards
        sections={favoritesSections}
        includes={props.contentSections.includes}
      /> */}
      {cta && (
        <StartEarningPerksMadi
          page={cta}
          includes={props.contentSections.includes}
        />
      )}
      <FooterV2 />
    </>
  );
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  const client = getContentfulClient("contentful");

  const howItWorksContent = await client.getEntries<GenericMarketingPage>({
    content_type: ContentfulContentType.GenericMarketingPage,
    ["fields.key"]: ContentfulKey.MainPerksPage,
  });

  const perksOfTheWeek = await client.getEntries<PerkOfTheWeekDocument>(
    RecentPerksOfTheWeek(15)
  );

  const page =
    howItWorksContent.items.length > 0 ? howItWorksContent.items[0] : null;
  const pageSections = compact(
    page?.fields.pageSections?.map((section) =>
      getItemReference(section, howItWorksContent.includes)
    )
  );

  const contentSections = await loadContentSections(client, pageSections);

  return {
    props: {
      pageContent: howItWorksContent,
      pageSections,
      contentSections,
      perksOfTheWeek: perksOfTheWeek,
    },
    revalidate: 100,
  };
};

export default Perks;
